import type { ReactNode } from 'react';
import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { get, kebabCase } from 'lodash';
import type { FieldProps } from 'formik';
import 'formik';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { getValidationStatus } from 'Components/frameworks/formik/inputsUtils';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
type Props = FieldProps & {
  ariaDescribedBy?: string;
  className?: string;
  dataTestId?: string;
  iconColor?: Colors;
  iconName: IconName;
  isDisabled: boolean;
  isFlex?: boolean;
  isRequired: boolean;
  label?: string | Node;
  onClick?: (arg0: React.SyntheticEvent) => void;
  placeholderComponent?: (isChecked: boolean) => Node;
};
export const Checkbox = ({
  ariaDescribedBy,
  className,
  dataTestId,
  field,
  form,
  iconColor = COLORS.BLACK,
  iconName,
  isDisabled = false,
  isRequired = false,
  label,
  isFlex = true,
  onChange,
  onClick,
  placeholderComponent,
}: Props) => {
  // Checkbox value defaults to false
  const value = get(form.values, field.name, false);
  const { isValidated, isErrored } = getValidationStatus({ ...field, value }, form, isDisabled);
  const checkboxId = `checkbox-${kebabCase(field.name)}`;
  return (
    <div
      className={classnames(
        'gap-10 text-14',
        {
          'formik-errored': isErrored && isRequired,
          'flex items-center justify-between': isFlex,
        },
        className,
      )}
    >
      {iconName && <Icon className={`shrink-0 text-${iconColor}`} name={iconName} width="1em" />}
      <label
        className={classnames('flex w-full items-start gap-10', {
          'cursor-pointer': !isDisabled,
          'cursor-not-allowed': isDisabled,
          'leading-0': !placeholderComponent,
        })}
        htmlFor={checkboxId}
      >
        <input
          aria-describedby={ariaDescribedBy}
          className="sr-only"
          data-testid={dataTestId}
          disabled={isDisabled}
          id={checkboxId}
          type="checkbox"
          {...field}
          checked={value}
          onChange={(e) => {
            field.onChange(e);

            if (onChange) {
              onChange(e);
            }
          }}
          onClick={onClick ? onClick : field.onClick}
        />
        {!placeholderComponent && (
          <span
            className={classnames(
              'outline-custom-sibling flex h-24 w-24 shrink-0 items-center justify-center rounded-8 border p-2',
              {
                'border-black': !isDisabled,
                'border-grey bg-pearl': isDisabled,
                'text-success': isValidated && isRequired,
                'formik-errored text-red': isErrored && isRequired,
              },
            )}
          >
            {!!value && <Icon className="text-black" name={ICONS.CHECK} width="1em" />}
          </span>
        )}
        {label && (
          <span className="mt-1 inline-block text-14 font-bold leading-normal text-black">
            <span>{label}</span>
            {isRequired && '*'}
          </span>
        )}
        {placeholderComponent && placeholderComponent(value)}
      </label>
    </div>
  );
};
